import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'deliverer_token';

const dax = axios.create({ baseURL: API });
dax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function shipmentTotal(s) {
  const t = Number(s?.total);
  if (Number.isFinite(t) && t > 0) return t;
  return Number(s?.items_total || 0) + Number(s?.shipping_cost || 0);
}

/**
 * Historial de envíos ya cobrados/pagados del entregador.
 * Últimos 50 por defecto.
 */
export default function DelivererPaidHistory({ onBack }) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await dax.get('/public/deliverer/paid-shipments?limit=100');
        setShipments(data.shipments || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudo cargar el historial');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const totalCollected = shipments.reduce((acc, s) => acc + shipmentTotal(s), 0);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-red-600 text-white px-4 pb-3 shadow flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button onClick={onBack} className="text-white/90 hover:text-white text-3xl leading-none w-10 h-10 flex items-center justify-center -ml-2 rounded-full active:bg-red-700" data-testid="history-back-btn" aria-label="Volver">‹</button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wide opacity-80">Portal Entregador</p>
          <h1 className="text-base sm:text-lg font-bold truncate">✅ Historial pagado ({shipments.length})</h1>
        </div>
      </header>

      {error && <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-xs text-rose-800" data-testid="history-error">{error}</div>}

      {shipments.length > 0 && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 text-xs text-emerald-800 text-center" data-testid="history-total">
          💰 Total cobrado en estos envíos: <b>${totalCollected.toFixed(2)}</b>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-2 max-w-3xl w-full mx-auto">
        {loading && <p className="text-sm text-slate-500 text-center py-8">Cargando…</p>}
        {!loading && shipments.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-10" data-testid="history-empty">Aún no tienes envíos cobrados.</p>
        )}
        {shipments.map((s) => {
          const isOpen = expanded.has(s.id);
          return (
            <div key={s.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`history-item-${s.id}`}>
              <button
                onClick={() => toggle(s.id)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50"
              >
                <div className="flex-shrink-0 text-emerald-500 text-xl">✅</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{s.client_name || 'Cliente'}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                    {s.folio && <span>#{s.folio}</span>}
                    <span>💰 ${shipmentTotal(s).toFixed(2)}</span>
                    {s.payment_method && <span>💳 {s.payment_method}</span>}
                    <span>{fmtDate(s.paid_at)}</span>
                  </div>
                </div>
                <div className="text-slate-400 text-xs">{isOpen ? '▲' : '▼'}</div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                  {(s.shipping_address || s.shipping_colonia) && (
                    <p className="text-xs text-slate-600 mb-2">📍 {s.shipping_address || s.shipping_colonia}</p>
                  )}
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Productos</p>
                  <ul className="space-y-1">
                    {(s.items || []).map((it, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <span className="w-8 h-6 flex items-center justify-center bg-slate-200 rounded text-[11px] font-semibold text-slate-600">
                          {it.quantity}×
                        </span>
                        <span className="flex-1 truncate">{it.product_name || it.name || 'Producto'}{it.variant_name ? ` — ${it.variant_name}` : ''}</span>
                      </li>
                    ))}
                    {(!s.items || s.items.length === 0) && (
                      <li className="text-xs text-slate-400 italic">Sin productos registrados</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
